import { useMemo, useState } from 'react';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { PrintButton } from '@/components/common/PrintButton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { cn } from '@/lib/utils';
import {
  exportSpecimenAdjustment,
  specimenDistributionAllRowsQueryOptions,
  specimenDistributionInfiniteQueryOptions,
} from '../api/product.api';
import { ProductFilterPanel } from '../components/ProductFilterPanel';
import { getSpecimenAdjustmentColumns } from '../components/ProductColumns';
import { SpecimenAdjustmentPrintReport } from '../components/SpecimenAdjustmentPrintReport';
import {
  SPECIMEN_DRILL_ORIGIN,
  SPECIMEN_REPORT_SEARCH_PLACEHOLDER,
} from '../constants/product.constants';
import { useDistributionFilters } from '../hooks/useDistributionFilters';
import { getDistributedPercent, getInHandQty } from '../utils/distribution';
import { getInstitutionNoFilterField, getPhoneFilterField } from '../utils/productFilterFields';

const PAGE_FILTER_FIELDS = [getPhoneFilterField('Teacher Phone No'), getInstitutionNoFilterField()];

export function SpecimenAdjustmentPage() {
  const controller = useDistributionFilters('adjustment');
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(specimenDistributionInfiniteQueryOptions(controller.params));
  const lastViewedSpecimenId = useRevealLastViewedRow('specimens');
  const queryClient = useQueryClient();
  const allRowsQueryOptions = specimenDistributionAllRowsQueryOptions(controller.params);
  const { data: allRows } = useQuery({ ...allRowsQueryOptions, enabled: false });
  const pickedZmId = controller.params.userId;
  const { data: pickedZm } = useQuery({
    ...userDetailQueryOptions(String(pickedZmId ?? 0)),
    enabled: pickedZmId !== undefined,
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { search } = controller.params;
  const { drillParams } = controller;
  const columns = useMemo(
    () =>
      getSpecimenAdjustmentColumns(
        { ...drillParams, from: SPECIMEN_DRILL_ORIGIN.adjustment },
        search,
      ),
    [drillParams, search],
  );

  const totals = firstPageMeta?.totals;
  const distributedPercent = totals
    ? getDistributedPercent(totals.distributedQty, totals.challanQty)
    : undefined;

  return (
    <>
      <div className={cn(FIT_TO_SCREEN_PAGE_CLASS, 'print:hidden')}>
        <PageHeader
          title="Specimen Adjustment"
          titleAddon={
            <DateRangeBadge
              onClick={() => setIsFilterOpen(true)}
              dateFrom={controller.params.dateFrom}
              dateTo={controller.params.dateTo}
            />
          }
          search={{
            value: controller.searchText,
            onChange: controller.setSearchText,
            placeholder: SPECIMEN_REPORT_SEARCH_PLACEHOLDER,
          }}
          actions={
            <>
              <AdvancedFilterButton
                isOpen={isFilterOpen}
                activeCount={controller.activeFilterCount}
                onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
              />
              <ExportButton
                requires={PRODUCT_PERMISSIONS.SPECIMEN_DISTRIBUTION_EXPORT}
                onExport={() => exportSpecimenAdjustment(controller.params)}
                disabled={isLoading || loadError !== null}
              />
              <PrintButton
                onBeforePrint={async () => {
                  await queryClient.fetchQuery(allRowsQueryOptions);
                }}
                disabled={isLoading || loadError !== null}
              />
            </>
          }
        />

        <FilterPanelCollapse isOpen={isFilterOpen}>
          <ProductFilterPanel filters={controller} pageFields={PAGE_FILTER_FIELDS} />
        </FilterPanelCollapse>

        {loadError ? (
          <ErrorState
            error={loadError}
            fallbackTitle="Could not load the adjustment"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <>
            <StatCardGrid
              isLoading={isLoading}
              stats={[
                {
                  key: 'challan',
                  label: 'Challan Qty',
                  value: totals?.challanQty,
                },
                {
                  key: 'distributed',
                  label: 'Distributed Qty',
                  value: totals?.distributedQty,
                },
                {
                  key: 'inHand',
                  label: 'Hand Qty',
                  value: totals
                    ? getInHandQty(totals.challanQty, totals.distributedQty)
                    : undefined,
                },
                {
                  key: 'distributedPercent',
                  label: 'Distributed (%)',
                  value: distributedPercent,
                  unit: '%',
                },
              ]}
            />

            <DataTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              className="lg:flex-1"
              stickyColumnId="name"
              isRowHighlighted={(specimen) => specimen.specimenId === lastViewedSpecimenId}
              emptyMessage="No book matches these criteria"
              emptyHint="Try a wider date range."
              {...loadMoreProps}
            />
          </>
        )}
      </div>

      <SpecimenAdjustmentPrintReport
        rows={allRows?.data ?? []}
        totals={allRows?.meta.totals}
        dateFrom={controller.params.dateFrom}
        dateTo={controller.params.dateTo}
        zm={pickedZm}
      />
    </>
  );
}
